import React from "react";
import { graphql, useFragment } from "react-relay";

import { useMeetingRecorderStatus } from "~/meetingRecorder/utils";

import type { useShouldShowRecapTab_note$key } from "~/__generated__/useShouldShowRecapTab_note.graphql";

/** A helper hook for determining whether to show the recap tab, since it's not straightforward. */
export function useShouldShowRecapTab(noteKey: useShouldShowRecapTab_note$key | null): boolean {
	const note = useFragment(
		graphql`
			fragment useShouldShowRecapTab_note on Note {
				hasMeetingRecordings
				meetingRecordings {
					isCurrentlyRecording
				}
				...utils_useMeetingRecorderStatus_note
			}
		`,
		noteKey,
	);

	const { hasMeetingRecordings, meetingRecordings } = note ?? {};

	// The current note may have a meetingRecording record because a recording is in progress,
	// but we don't want to show the Recap tab until transcription has finished
	const recorderStatus = useMeetingRecorderStatus(note);
	const shouldShowRecapTab = React.useMemo(() => {
		const isSingleRecording = hasMeetingRecordings && meetingRecordings?.length === 1;
		if (isSingleRecording) {
			const singleRecordingIsInProgress =
				meetingRecordings.every(recording => recording.isCurrentlyRecording) ||
				(recorderStatus.status &&
					["IN_WAITING_ROOM", "JOINED_CALL", "RECORDING", "SCHEDULED"].includes(recorderStatus.status));
			return !singleRecordingIsInProgress;
		}

		return Boolean(hasMeetingRecordings);
	}, [hasMeetingRecordings, meetingRecordings, recorderStatus.status]);

	return shouldShowRecapTab;
}
